/**
 * 工厂端 - 资金账户
 *
 * 工厂视角：货款收入 = amount_total × (1 - commission_rate)。
 * 汇总 SETTLED/INVOICED/COMPLETED 订单，列出每笔订单的货款分成。
 */

import { useState } from 'react'
import { Card, Table, Badge, useApi, money, time, Loading } from '../../components/ui'
import { HelpBubble } from '../../components/HelpBubble'
import { OrderDetailModal } from '../../components/OrderDetailModal'
import { api } from '../../api/client'
import { WithdrawCard } from '../seller/Finance'

interface Merchant {
  merchant_id: string
  company_name: string
  bank_sub_account: string | null
  status: string
  tax_no: string
}

interface Order {
  order_id: string
  partnership_id: string
  amount_total: string
  status: string
  synced_at: string
  logistics_no: string | null
}

interface Partnership {
  partnership_id: string
  commission_rate: string
}

/** 计入货款收入的订单状态：PAID（已支付即已分账）；REFUNDED 不计入 */
const REVENUE_STATES = new Set(['PAID'])

function isSameMonth(iso: string): boolean {
  if (!iso) return false
  const d = new Date(iso)
  const now = new Date()
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
}

export function FactoryFinance() {
  const me = useApi<Merchant>(async () => api.get('/merchants/me'), [])
  const orders = useApi<Order[]>(async () => api.get('/orders'), [])
  const partnerships = useApi<Partnership[]>(async () => api.get('/partnerships'), [])
  const [orderDetail, setOrderDetail] = useState<string | null>(null)

  // partnership_id -> commission_rate
  const rateMap = new Map<string, number>()
  for (const p of partnerships.data ?? []) {
    rateMap.set(p.partnership_id, Number(p.commission_rate))
  }

  const revenueOrders = (orders.data ?? []).filter((o) => REVENUE_STATES.has(o.status))

  const totalRevenue = revenueOrders.reduce((sum, o) => {
    const rate = rateMap.get(o.partnership_id) ?? 0
    return sum + Number(o.amount_total) * (1 - rate)
  }, 0)

  const monthRevenue = revenueOrders
    .filter((o) => isSameMonth(o.synced_at))
    .reduce((sum, o) => {
      const rate = rateMap.get(o.partnership_id) ?? 0
      return sum + Number(o.amount_total) * (1 - rate)
    }, 0)

  if (me.loading) return <Loading />
  if (me.error) return <Loading error={me.error} />

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>资金账户</h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 16 }}>
        <MetricCard label="虚拟子户号" value={me.data?.bank_sub_account ?? '未开通'} />
        <MetricCard label="本月货款收入" value={money(monthRevenue)} />
        <MetricCard label="累计货款收入" value={money(totalRevenue)} />
      </div>

      <WithdrawCard merchantId={me.data?.merchant_id} />

      <Card title="主体信息">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, fontSize: 13 }}>
          <Field label="主体ID" value={me.data?.merchant_id ?? '-'} />
          <Field label="主体名称" value={me.data?.company_name ?? '-'} />
          <Field label="税号" value={me.data?.tax_no ?? '-'} />
          <Field label="虚拟子户号" value={me.data?.bank_sub_account ?? '未开通'} />
          <Field label="主体状态" value={me.data?.status ?? '-'} />
        </div>
      </Card>

      <Card title={`订单货款明细（${revenueOrders.length} 笔已分账）`}>
        {orders.loading ? (
          <Loading />
        ) : (
          <Table<Order>
            rows={orders.data ?? []}
            empty="暂无订单"
            columns={[
              { key: 'order_id', title: '订单号', render: (r) => <a onClick={() => setOrderDetail(r.order_id)} style={{ color: '#2563eb', cursor: 'pointer' }}>{r.order_id}</a> },
              { key: 'synced_at', title: '同步时间', render: (r) => time(r.synced_at) },
              { key: 'amount_total', title: '订单金额', render: (r) => money(r.amount_total) },
              {
                key: 'rate',
                title: '佣金率',
                render: (r) => {
                  const rate = rateMap.get(r.partnership_id)
                  return rate != null ? `${(rate * 100).toFixed(1)}%` : '-'
                },
              },
              {
                key: 'net',
                title: '工厂货款',
                render: (r) => {
                  const rate = rateMap.get(r.partnership_id) ?? 0
                  return money(Number(r.amount_total) * (1 - rate))
                },
              },
              { key: 'status', title: '状态', render: (r) => <Badge status={r.status} /> },
            ]}
          />
        )}
      </Card>

      <HelpBubble pageKey="factory-finance" title="资金账户">
        <p>工厂视角：货款 = 订单金额 ×（1 - 佣金率）。佣金率是商家拿的服务费占比，剩余是工厂货款。</p>
        <p>分账后货款自动到工厂子户，可在资金账户提现。点订单号看订单详情。</p>
      </HelpBubble>

      {orderDetail && <OrderDetailModal orderId={orderDetail} onClose={() => setOrderDetail(null)} />}
    </div>
  )
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: '#fff', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' }}>
      <div style={{ color: '#888', fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ color: '#888', fontSize: 12 }}>{label}</div>
      <div style={{ fontWeight: 500 }}>{value}</div>
    </div>
  )
}
